import React, { useCallback, useEffect, useState } from 'react';
import BodyHeatMap from './BodyHeatMap';
import CoachNotes from './CoachNotes';
import { errorText, formatKg, formatNumber } from './strengthFormat';
import './InsightsPanel.css';

const LEVELS = ['Untrained', 'Beginner', 'Novice', 'Intermediate', 'Advanced', 'Elite'];

// Turns "[E1, E2]" into links to the cited sources.
export function CitedText({ text, evidence }) {
  const parts = String(text || '').split(/(\[E\d+(?:\s*,\s*E\d+)*\])/g);
  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/^\[(.+)\]$/);
        if (!match) return <React.Fragment key={i}>{part}</React.Fragment>;
        const ids = match[1].split(',').map(s => s.trim());
        return (
          <sup key={i} className="st-cite">
            [{ids.map((id, j) => {
              const source = (evidence || []).find(e => e.id === id);
              return (
                <React.Fragment key={id}>
                  {j > 0 && ', '}
                  {source ? <a href={source.url} target="_blank" rel="noopener noreferrer" title={source.citation}>{id}</a> : id}
                </React.Fragment>
              );
            })}]
          </sup>
        );
      })}
    </>
  );
}

export function SourceList({ t, evidence }) {
  if (!evidence?.length) return null;
  return (
    <details className="st-sources">
      <summary>{t('strength.insights.sources')}</summary>
      <ol>
        {evidence.map(e => (
          <li key={e.id} id={`source-${e.id}`}>
            <strong>[{e.id}]</strong> {e.citation}{' '}
            <a href={e.url} target="_blank" rel="noopener noreferrer">{t('strength.insights.open')}</a>
            <div className="st-muted st-small">{e.summary}</div>
          </li>
        ))}
      </ol>
    </details>
  );
}

function ProfileForm({ api, t, showMessage, onSaved }) {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.profile().then(p => setProfile(p || {})).catch(() => setProfile({}));
  }, [api]);

  if (!profile) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.saveProfile({
        sex: profile.sex || null,
        birthYear: profile.birthYear ? Number(profile.birthYear) : null,
        bodyweightKg: profile.bodyweightKg ? Number(profile.bodyweightKg) : null
      });
      showMessage(t('strength.profile.saved'));
      onSaved();
    } catch (err) {
      showMessage(errorText(err, t('strength.errors.save')), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="st-profile-form" onSubmit={save} aria-label={t('strength.profile.title')}>
      <label className="st-field">
        <span>{t('strength.profile.sex')}</span>
        <select className="st-select" value={profile.sex || ''} onChange={(e) => setProfile(p => ({ ...p, sex: e.target.value || null }))}>
          <option value="">—</option>
          <option value="Female">{t('strength.profile.female')}</option>
          <option value="Male">{t('strength.profile.male')}</option>
        </select>
      </label>
      <label className="st-field">
        <span>{t('strength.profile.birthYear')}</span>
        <input className="st-input" type="number" inputMode="numeric" min="1920" max={new Date().getFullYear() - 13}
          value={profile.birthYear || ''} onChange={(e) => setProfile(p => ({ ...p, birthYear: e.target.value }))} />
      </label>
      <label className="st-field">
        <span>{t('strength.profile.bodyweight')}</span>
        <input className="st-input" type="number" inputMode="decimal" min="25" max="300" step="0.1"
          value={profile.bodyweightKg || ''} onChange={(e) => setProfile(p => ({ ...p, bodyweightKg: e.target.value }))} />
      </label>
      <button type="submit" className="st-btn st-btn-primary" disabled={saving}>{t('strength.common.save')}</button>
      <p className="st-muted st-small st-profile-privacy">🔒 {t('strength.profile.privacy')}</p>
    </form>
  );
}

function LiftLevels({ t, lang, lifts, evidence }) {
  if (!lifts?.length) return null;
  return (
    <div className="st-levels">
      {lifts.map(l => {
        const index = LEVELS.indexOf(l.level);
        return (
          <article key={l.exerciseKey} className="st-level-card">
            <div className="st-row st-row-between">
              <strong>{l.exerciseName}</strong>
              <span className={`st-badge st-level-${l.level}`}>{t(`strength.levels.${l.level}`)}</span>
            </div>
            <div className="st-level-bar" aria-hidden="true">
              {LEVELS.slice(1).map((level, i) => <span key={level} className={i < index ? 'on' : ''} />)}
            </div>
            <p className="st-small">
              {t('strength.insights.liftLine', { kg: formatKg(l.estimatedMaxKg, lang), ratio: formatNumber(l.bodyweightRatio, lang) })}
              {l.ageCoefficient > 1 && <> · {t('strength.insights.ageAdjusted', { factor: formatNumber(l.ageCoefficient, lang, 3) })}</>}
              {' '}<CitedText text={l.ageCoefficient > 1 ? '[E5, E4]' : '[E5]'} evidence={evidence} />
            </p>
            {l.nextLevel && l.nextLevelKg && (
              <p className="st-small st-muted">
                {t('strength.insights.nextLevel', { level: t(`strength.levels.${l.nextLevel}`), kg: formatKg(l.nextLevelKg, lang), gap: formatKg(Math.max(0, l.nextLevelKg - l.estimatedMaxKg), lang) })}
              </p>
            )}
            {l.progressPercent != null && (
              <p className={`st-small ${l.progressPercent >= 0 ? 'st-good' : 'st-muted'}`}>
                {t('strength.insights.trend', { percent: `${l.progressPercent >= 0 ? '+' : ''}${formatNumber(l.progressPercent, lang, 1)}`, weeks: l.progressWeeks })}
              </p>
            )}
          </article>
        );
      })}
    </div>
  );
}

/**
 * The training picture: a body heat map of weekly sets per muscle, what has gone well,
 * what to add next, and, for yourself, strength levels from your private profile.
 */
function InsightsPanel({ api, t, lang, userId, showMessage, compact = false, onOpenProgress }) {
  const [days, setDays] = useState(7);
  const [insights, setInsights] = useState(null);
  const [showProfile, setShowProfile] = useState(false);

  const load = useCallback(async () => {
    try {
      setInsights(await api.insights(userId, days));
    } catch (e) {
      setInsights({ muscles: [], wentWell: [], nextFocus: [], lifts: [], evidence: [] });
      showMessage?.(errorText(e, t('strength.errors.load')), 'error');
    }
  }, [api, userId, days, showMessage, t]);

  useEffect(() => { load(); }, [load]);

  if (!insights) return <div className="st-card st-empty">{t('strength.loading')}</div>;
  const { evidence } = insights;

  return (
    <section className={`st-card st-insights ${compact ? 'compact' : ''}`}>
      <div className="st-row st-row-between">
        <h3>🔥 {t('strength.heat.title')}</h3>
        <div className="st-chips st-chips-tight" role="group" aria-label={t('strength.heat.window')}>
          {[7, 28].map(d => (
            <button key={d} type="button" className={`st-chip ${days === d ? 'active' : ''}`} onClick={() => setDays(d)}>
              {t(d === 7 ? 'strength.heat.thisWeek' : 'strength.heat.fourWeeks')}
            </button>
          ))}
        </div>
      </div>
      <p className="st-muted st-small">{t(days === 7 ? 'strength.heat.explainWeek' : 'strength.heat.explainMonth')}</p>

      <div className="st-insights-grid">
        <BodyHeatMap t={t} muscles={insights.muscles} compact={compact} />

        <div className="st-insights-text">
          {insights.wentWell.length > 0 && (
            <>
              <h4>✅ {t('strength.insights.wentWell')}</h4>
              <ul>{insights.wentWell.map((w, i) => <li key={i}><CitedText text={w} evidence={evidence} /></li>)}</ul>
            </>
          )}
          <h4>➕ {t('strength.insights.nextFocus')}</h4>
          <ul>
            {insights.nextFocus.map((f, i) => (
              <li key={i}>
                <CitedText text={f.text} evidence={evidence} />
                {f.exercises.length > 0 && (
                  <div className="st-chips st-chips-tight">
                    {f.exercises.map(e => <span key={e.key} className="st-chip st-chip-static">{e.name}</span>)}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {compact && onOpenProgress && (
            <button type="button" className="st-link-btn" onClick={onOpenProgress}>{t('strength.insights.more')} →</button>
          )}
        </div>
      </div>

      {!compact && insights.isMine && <CoachNotes api={api} t={t} lang={lang} showMessage={showMessage} />}

      {!compact && insights.isMine && (
        <div className="st-insights-levels">
          <div className="st-row st-row-between">
            <h4>🏅 {t('strength.insights.levels')}</h4>
            <button type="button" className="st-link-btn" onClick={() => setShowProfile(s => !s)}>
              {insights.profileComplete ? t('strength.profile.edit') : t('strength.profile.add')}
            </button>
          </div>
          {!insights.profileComplete && !showProfile && <p className="st-muted st-small">{t('strength.profile.why')}</p>}
          {showProfile && <ProfileForm api={api} t={t} showMessage={showMessage} onSaved={() => { setShowProfile(false); load(); }} />}
          {insights.profileComplete && insights.lifts.length === 0 && <p className="st-muted st-small">{t('strength.insights.noStandardLifts')}</p>}
          <LiftLevels t={t} lang={lang} lifts={insights.lifts} evidence={evidence} />
        </div>
      )}

      {!compact && <SourceList t={t} evidence={evidence} />}
    </section>
  );
}

export default InsightsPanel;
